/**
 * Governance Recommendations - Organization-Level Recommendations Tab
 *
 * Displays recommendations generated from:
 * - Interview sentiment analysis (People Readiness)
 * - Governance document analysis (Governance Maturity)
 * - Standards compliance validation (Interoperability Maturity)
 *
 * Features:
 * - Filter by source (interview/document/standard)
 * - Filter by priority (critical/high/medium/low)
 * - Filter by status (active/addressed/dismissed)
 * - Expand to view details, rationale, implementation steps
 * - Mark as addressed/dismissed with tracking
 *
 * @version 1.0.0
 * @date 2025-12-05
 */

import React, { useState, useEffect, useCallback } from 'react';
import './governance-layout.css';
import './organizational-interviews.css'; // Shared styles for governance pages
import './governance-recommendations.css';

// Types matching API response
interface PistoiaDriver {
  driver_id: string | null;
  driver_name: string | null;
  category: string | null;
}

interface Recommendation {
  recommendation_id: string;
  source_type: 'interview' | 'document' | 'standard' | 'execution_risk';
  source_id: string | null;
  source_name: string | null;  // Persona for interviews, document name for documents
  pistoia_driver: PistoiaDriver;
  title: string;
  description: string;
  rationale: string | null;
  priority: 'critical' | 'high' | 'medium' | 'low';
  impact: string | null;
  effort: string | null;
  timeline: string | null;
  related_fair_rules: string[];
  affected_dimensions: string[];
  implementation_steps: Array<string | { step: string; complexity?: string }>;
  stakeholder_context: string | null;
  calculation_hints: Record<string, unknown> | null;
  status: 'active' | 'addressed' | 'dismissed' | 'superseded';
  created_at: string;
  updated_at: string | null;
}

// Grouped recommendation - multiple sources for same title
interface GroupedRecommendation extends Omit<Recommendation, 'source_id' | 'source_name' | 'recommendation_id'> {
  recommendation_ids: string[];
  sources: Array<{
    source_type: string;
    source_id: string | null;
    source_name: string | null;
  }>;
}

interface RecommendationsSummary {
  active_count: number;
  addressed_count: number;
  by_priority: {
    critical: number;
    high: number;
    medium: number;
    low: number;
  };
  by_source: {
    interview: number;
    document: number;
    standard: number;
  };
  last_generated: string | null;
}

type FilterStatus = 'active' | 'addressed' | 'dismissed' | 'all';
type FilterSource = 'all' | 'interview' | 'document' | 'standard';
type FilterPriority = 'all' | 'critical' | 'high' | 'medium' | 'low';

export const GovernanceRecommendations: React.FC = () => {
  const [recommendations, setRecommendations] = useState<Recommendation[]>([]);
  const [summary, setSummary] = useState<RecommendationsSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [statusFilter, setStatusFilter] = useState<FilterStatus>('active');
  const [sourceFilter, setSourceFilter] = useState<FilterSource>('all');
  const [priorityFilter, setPriorityFilter] = useState<FilterPriority>('all');

  // Expanded recommendations
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  // Load recommendations
  const loadRecommendations = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams();
      if (statusFilter !== 'all') {
        params.set('status', statusFilter);
      }
      if (sourceFilter !== 'all') {
        params.set('source_type', sourceFilter);
      }

      const response = await fetch(`/api/organization/recommendations?${params.toString()}`);

      if (!response.ok) {
        throw new Error(`Failed to load recommendations: ${response.statusText}`);
      }

      const data = await response.json();
      if (data.success) {
        setRecommendations(data.recommendations || []);
        setSummary(data.summary || null);
      } else {
        throw new Error(data.error || 'Failed to load recommendations');
      }
    } catch (err: unknown) {
      console.error('[GovernanceRecommendations] Load error:', err);
      setError(err instanceof Error ? err.message : "Unknown error");
    } finally {
      setLoading(false);
    }
  }, [statusFilter, sourceFilter]);

  useEffect(() => {
    loadRecommendations();
  }, [loadRecommendations]);

  // Toggle expanded state
  const toggleExpanded = (id: string) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  // Group recommendations by title to deduplicate
  const groupRecommendations = (recs: Recommendation[]): GroupedRecommendation[] => {
    const groupMap = new Map<string, GroupedRecommendation>();

    recs.forEach((rec) => {
      // Use normalized title as grouping key
      const key = rec.title.toLowerCase().trim();

      if (groupMap.has(key)) {
        // Add to existing group
        const existing = groupMap.get(key)!;
        existing.recommendation_ids.push(rec.recommendation_id);
        existing.sources.push({
          source_type: rec.source_type,
          source_id: rec.source_id,
          source_name: rec.source_name,
        });
      } else {
        // Create new group
        const { source_id, source_name, recommendation_id, ...rest } = rec;
        groupMap.set(key, {
          ...rest,
          recommendation_ids: [recommendation_id],
          sources: [{
            source_type: rec.source_type,
            source_id: source_id,
            source_name: source_name,
          }],
        });
      }
    });

    return Array.from(groupMap.values());
  };

  // Filter recommendations by priority (client-side)
  const filteredRecommendations = recommendations.filter(rec => {
    if (priorityFilter !== 'all' && rec.priority !== priorityFilter) {
      return false;
    }
    return true;
  });

  // Group filtered recommendations
  const groupedRecommendations = groupRecommendations(filteredRecommendations);

  // Get source icon
  const getSourceIcon = (source: string): string => {
    switch (source) {
      case 'interview': return '🎤';
      case 'document': return '📄';
      case 'standard': return '🔗';
      case 'execution_risk': return '⚠️';
      default: return '📋';
    }
  };

  // Get source label
  const getSourceLabel = (source: string): string => {
    switch (source) {
      case 'interview': return 'Interview';
      case 'document': return 'Document';
      case 'standard': return 'Standard';
      case 'execution_risk': return 'Execution Risk';
      default: return source;
    }
  };

  // Get priority class
  const getPriorityClass = (priority: string): string => {
    return `priority--${priority}`;
  };

  // Format date
  const formatDate = (dateStr: string): string => {
    try {
      return new Date(dateStr).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
    } catch {
      return dateStr;
    }
  };

  if (loading) {
    return (
      <div className="governance-loading">
        <div className="spinner" />
        <p>Loading recommendations...</p>
      </div>
    );
  }

  return (
    <div className="governance-section governance-recommendations">
      {/* Header */}
      <div className="governance-section__header">
        <div className="governance-section__title">
          <h2 style={{ color: '#1a1a1a' }}>Organizational Recommendations</h2>
          <p className="governance-section__subtitle" style={{ color: '#4a4a4a' }}>
            Actions identified from interviews, documents, and standards
          </p>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="governance-error">
          <span className="governance-error__icon">!</span>
          <span className="governance-error__message">{error}</span>
          <button onClick={() => setError(null)} className="governance-error__dismiss">
            X
          </button>
        </div>
      )}

      {/* Summary Stats */}
      {summary && (
        <div className="rec-summary-bar">
          <div className="rec-summary-stats">
            <div className="rec-stat-item rec-stat-item--total">
              <span className="rec-stat-value">{summary.active_count}</span>
              <span className="rec-stat-label">Active</span>
            </div>
            <div className="rec-stat-item rec-stat-item--addressed">
              <span className="rec-stat-value">{summary.addressed_count}</span>
              <span className="rec-stat-label">Addressed</span>
            </div>
            <div className="rec-stat-divider" />
            {summary.by_priority.critical > 0 && (
              <div className="rec-stat-item rec-stat-item--critical">
                <span className="rec-stat-value">{summary.by_priority.critical}</span>
                <span className="rec-stat-label">Critical</span>
              </div>
            )}
            {summary.by_priority.high > 0 && (
              <div className="rec-stat-item rec-stat-item--high">
                <span className="rec-stat-value">{summary.by_priority.high}</span>
                <span className="rec-stat-label">High</span>
              </div>
            )}
            {summary.by_priority.medium > 0 && (
              <div className="rec-stat-item rec-stat-item--medium">
                <span className="rec-stat-value">{summary.by_priority.medium}</span>
                <span className="rec-stat-label">Medium</span>
              </div>
            )}
            {summary.by_priority.low > 0 && (
              <div className="rec-stat-item rec-stat-item--low">
                <span className="rec-stat-value">{summary.by_priority.low}</span>
                <span className="rec-stat-label">Low</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="rec-filters">
        <div className="rec-filter-group">
          <label className="rec-filter-label">Status</label>
          <select
            className="rec-filter-select"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as FilterStatus)}
          >
            <option value="active">Active</option>
            <option value="addressed">Addressed</option>
            <option value="dismissed">Dismissed</option>
            <option value="all">All</option>
          </select>
        </div>

        <div className="rec-filter-group">
          <label className="rec-filter-label">Source</label>
          <select
            className="rec-filter-select"
            value={sourceFilter}
            onChange={(e) => setSourceFilter(e.target.value as FilterSource)}
          >
            <option value="all">All Sources</option>
            <option value="interview">Interviews ({summary?.by_source.interview || 0})</option>
            <option value="document">Documents ({summary?.by_source.document || 0})</option>
            <option value="standard">Standards ({summary?.by_source.standard || 0})</option>
          </select>
        </div>

        <div className="rec-filter-group">
          <label className="rec-filter-label">Priority</label>
          <select
            className="rec-filter-select"
            value={priorityFilter}
            onChange={(e) => setPriorityFilter(e.target.value as FilterPriority)}
          >
            <option value="all">All Priorities</option>
            <option value="critical">Critical</option>
            <option value="high">High</option>
            <option value="medium">Medium</option>
            <option value="low">Low</option>
          </select>
        </div>

        <button
          className="rec-refresh-btn"
          onClick={loadRecommendations}
          disabled={loading}
        >
          Refresh
        </button>
      </div>

      {/* Recommendations List */}
      <div className="rec-list">
        {groupedRecommendations.length === 0 ? (
          <div className="rec-empty">
            <div className="rec-empty__icon">📋</div>
            <h4>No recommendations found</h4>
            <p>
              {statusFilter === 'active'
                ? 'Upload interviews or documents to generate recommendations.'
                : `No ${statusFilter} recommendations match your filters.`}
            </p>
          </div>
        ) : (
          groupedRecommendations.map((rec) => {
            // Use first recommendation_id as the key and for expansion tracking
            const groupKey = rec.recommendation_ids[0];
            const isExpanded = expandedIds.has(groupKey);

            return (
              <div
                key={groupKey}
                className={`rec-card ${isExpanded ? 'rec-card--expanded' : ''}`}
              >
                {/* Card Header - Always Visible */}
                <div
                  className="rec-card__header"
                  onClick={() => toggleExpanded(groupKey)}
                >
                  <div className="rec-card__priority">
                    <span className={`rec-priority-badge ${getPriorityClass(rec.priority)}`}>
                      {rec.priority}
                    </span>
                  </div>

                  <div className="rec-card__main">
                    <h4 className="rec-card__title">{rec.title}</h4>
                    <p className="rec-card__description">{rec.description}</p>

                    {/* Source tags - show persona/document names */}
                    <div className="rec-source-tags">
                      {rec.sources.map((source, idx) => (
                        <span
                          key={idx}
                          className={`rec-source-tag rec-source-tag--${source.source_type}`}
                          title={`From ${getSourceLabel(source.source_type)}${source.source_name ? `: ${source.source_name}` : ''}`}
                        >
                          {getSourceIcon(source.source_type)} {source.source_name || getSourceLabel(source.source_type)}
                        </span>
                      ))}
                    </div>
                  </div>

                  <div className="rec-card__meta">
                    {rec.pistoia_driver.driver_name && (
                      <span className="rec-driver-badge" title={`Pistoia Driver: ${rec.pistoia_driver.driver_name}`}>
                        {rec.pistoia_driver.category}
                      </span>
                    )}
                  </div>

                  <div className="rec-card__expand">
                    <span className="rec-expand-icon">{isExpanded ? '−' : '+'}</span>
                  </div>
                </div>

                {/* Expanded Content */}
                {isExpanded && (
                  <div className="rec-card__body">
                    {/* Rationale */}
                    {rec.rationale && (
                      <div className="rec-detail-section">
                        <h5 className="rec-detail-label">Rationale</h5>
                        <p className="rec-detail-text">{rec.rationale}</p>
                      </div>
                    )}

                    {/* Impact/Effort/Timeline Row */}
                    <div className="rec-impact-row">
                      {rec.impact && (
                        <div className="rec-impact-item">
                          <span className="rec-impact-label">Impact</span>
                          <span className={`rec-impact-value rec-impact-value--${rec.impact}`}>
                            {rec.impact}
                          </span>
                        </div>
                      )}
                      {rec.effort && (
                        <div className="rec-impact-item">
                          <span className="rec-impact-label">Effort</span>
                          <span className={`rec-impact-value rec-impact-value--${rec.effort}`}>
                            {rec.effort}
                          </span>
                        </div>
                      )}
                      {rec.timeline && (
                        <div className="rec-impact-item">
                          <span className="rec-impact-label">Timeline</span>
                          <span className="rec-impact-value">{rec.timeline}</span>
                        </div>
                      )}
                    </div>

                    {/* Related FAIR Rules */}
                    {rec.related_fair_rules && rec.related_fair_rules.length > 0 && (
                      <div className="rec-detail-section">
                        <h5 className="rec-detail-label">Related FAIR Rules</h5>
                        <div className="rec-fair-rules">
                          {rec.related_fair_rules.map((rule, idx) => (
                            <span key={idx} className="rec-fair-rule">{rule}</span>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Implementation Steps */}
                    {rec.implementation_steps && rec.implementation_steps.length > 0 && (
                      <div className="rec-detail-section">
                        <h5 className="rec-detail-label">Implementation Steps</h5>
                        <ol className="rec-steps-list">
                          {rec.implementation_steps.map((step, idx) => (
                            <li key={idx} className="rec-step">
                              {typeof step === 'string' ? step : step.step}
                              {typeof step === 'object' && step.complexity && (
                                <span className="rec-step-complexity"> ({step.complexity})</span>
                              )}
                            </li>
                          ))}
                        </ol>
                      </div>
                    )}

                    {/* Stakeholder Context */}
                    {rec.stakeholder_context && (
                      <div className="rec-detail-section">
                        <h5 className="rec-detail-label">Stakeholder Context</h5>
                        <p className="rec-detail-text rec-detail-text--muted">
                          {rec.stakeholder_context}
                        </p>
                      </div>
                    )}

                    {/* Footer with date and status actions */}
                    <div className="rec-card__footer">
                      <span className="rec-date">
                        Created: {formatDate(rec.created_at)}
                        {rec.sources.length > 1 && (
                          <span className="rec-multi-source-note">
                            {' '}(from {rec.sources.length} sources)
                          </span>
                        )}
                      </span>
                      {rec.status === 'active' && (
                        <div className="rec-actions">
                          <span className="rec-status-badge rec-status-badge--active">Active</span>
                        </div>
                      )}
                      {rec.status === 'addressed' && (
                        <span className="rec-status-badge rec-status-badge--addressed">Addressed</span>
                      )}
                      {rec.status === 'dismissed' && (
                        <span className="rec-status-badge rec-status-badge--dismissed">Dismissed</span>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* Help Section */}
      <div className="governance-help">
        <h4>About Organizational Recommendations</h4>
        <p>
          Recommendations are automatically generated when you analyze organizational data:
        </p>
        <ul>
          <li>
            <strong>Interview Analysis</strong> - Sentiment and theme analysis from stakeholder
            interviews identifies readiness gaps and change management needs.
          </li>
          <li>
            <strong>Document Analysis</strong> - FAIR scoring of governance documents reveals
            policy gaps and areas needing improvement.
          </li>
          <li>
            <strong>Standards Compliance</strong> - Validation against enabled interoperability
            standards highlights integration issues.
          </li>
        </ul>
        <p>
          Recommendations are pre-mapped to the Pistoia Alliance FAIR Implementation Framework
          for alignment with industry best practices.
        </p>
      </div>
    </div>
  );
};

export default GovernanceRecommendations;
